import { useMemo, useState } from 'react';
import { distance } from 'fastest-levenshtein';
import useSWR from 'swr';

import { OrderLineStopType } from 'src/types/enums';
import { InputWrapper } from '../../../components/InputWrapper';
import { Combobox } from '../../../components/combobox/Combobox';
import { useTranslation } from '../../../contexts/translation-context';
import { getDisplayError } from '../../../utils/get-display-error';
import { fetchCustomerPortalOrderLocations } from './models';

export type LocationComboValue = {
  id: string;
  postalCode: string;
  city: string;
  country: string;
};

export interface ICustomerPortalOrderLocationComboboxProps {
  value: LocationComboValue | null;
  onChange: (val: LocationComboValue | null) => void;
  orderLineStopType: OrderLineStopType;
  customerId: number;
  labelText?: string;
}

const displayLocation = (value: LocationComboValue) => {
  return `${value.country}-${value.postalCode} ${value.city}`;
};

const normalizeSearchValue = (value: string): string => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
};

const fetcher = (value: [string, OrderLineStopType, number]) => {
  return fetchCustomerPortalOrderLocations({ type: value[1], customerId: value[2] });
};

export const CustomerPortalOrderLocationCombobox: React.FC<ICustomerPortalOrderLocationComboboxProps> = (props) => {
  const { value, onChange, orderLineStopType, customerId, labelText } = props;
  const { i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { data, error, isLoading } = useSWR(['customerPortalOrderLocations', orderLineStopType, customerId], fetcher);

  const rawLocations = useMemo(() => {
    return (
      data?.locations?.map((v) => {
        const [country, postalCode, city] = v.split('::');

        return {
          id: v,
          country,
          postalCode,
          city,
          postalCodeSearchValue: normalizeSearchValue(`${country}${postalCode}`),
          citySearchValue: normalizeSearchValue(city),
        };
      }) || []
    );
  }, [data?.locations]);

  const locations: LocationComboValue[] = useMemo(() => {
    const normalizedSearchValue = normalizeSearchValue(searchValue);
    const hasPostalCode = /\d+/.test(searchValue);
    let filtered = rawLocations
      .filter((v) => {
        if (hasPostalCode) {
          return v.postalCodeSearchValue.includes(normalizedSearchValue);
        } else {
          return true;
        }
      })
      .map((v) => {
        return {
          ...v,
          distance: hasPostalCode
            ? distance(v.postalCodeSearchValue, normalizedSearchValue)
            : distance(v.citySearchValue, normalizedSearchValue),
        };
      })
      .sort((a, b) => a.distance - b.distance);

    return filtered.slice(0, 25).map((v) => {
      return {
        id: v.id,
        country: v.country,
        postalCode: v.postalCode,
        city: v.city,
      };
    });
  }, [rawLocations, searchValue]);

  return (
    <InputWrapper labelText={labelText}>
      <Combobox
        keyName="id"
        placeholder={i18n('customerPortal.locations.findLocation')}
        onQueryChange={setSearchValue}
        display={displayLocation}
        items={locations}
        selectedItem={value}
        onSelect={(val) => {
          onChange(val);
        }}
        isNullable={true}
        isLoading={isLoading}
      />
      {error ? <div className="text-sm text-negative-text mt-1">{getDisplayError(error)}</div> : null}
    </InputWrapper>
  );
};
